import React, { useState, useEffect, useRef } from 'react';
import { lintScript, applyFix, applyAllFixes } from '../utils/scriptLinter';
import './ScriptValidator.css';

const SEVERITY_ICON = { error: '✗', warning: '⚠', info: 'ℹ' };

export default function ScriptValidator({ text, onApplyFix, onApplyAllFixes }) {
  const [result, setResult] = useState({ issues: [], cleanedPreview: '' });
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);

  // Debounce lint at 300ms
  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setResult(lintScript(text));
    }, 300);
    return () => clearTimeout(timerRef.current);
  }, [text]);

  const { issues } = result;
  const errors   = issues.filter(i => i.severity === 'error').length;
  const warnings = issues.filter(i => i.severity === 'warning').length;
  const infos    = issues.filter(i => i.severity === 'info').length;
  const total    = issues.length;
  const fixable  = issues.filter(i => i.suggestion && i.original).length;

  const handleApplyOne = (issue) => {
    if (onApplyFix) {
      const fixed = applyFix(text, issue);
      onApplyFix(fixed);
    }
  };

  const handleFixAll = () => {
    if (onApplyAllFixes) {
      const fixed = applyAllFixes(text, issues);
      onApplyAllFixes(fixed);
    }
  };

  if (!text || !text.trim()) return null;

  // Badge
  const badgeClass = errors > 0 ? 'sv-badge--error'
    : warnings > 0 ? 'sv-badge--warning'
    : 'sv-badge--clean';
  const badgeText = errors > 0
    ? `✗ ${errors} error${errors > 1 ? 's' : ''}`
    : warnings > 0
      ? `⚠ ${warnings + infos} issue${(warnings + infos) > 1 ? 's' : ''}`
      : '✓ Clean';

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <div className={`sv-badge ${badgeClass}`} onClick={() => total > 0 && setOpen(v => !v)}>
        {badgeText}
      </div>

      {open && total > 0 && (
        <div className="sv-panel">
          <div className="sv-panel-header">
            <span className="sv-panel-title">{total} issue{total !== 1 ? 's' : ''} found</span>
            {fixable > 1 && (
              <button className="sv-fix-all-btn" onClick={handleFixAll}>
                Fix all ({fixable})
              </button>
            )}
          </div>
          <div className="sv-list">
            {issues.map((issue, i) => (
              <div key={i} className="sv-item">
                <div className={`sv-icon sv-icon--${issue.severity}`}>
                  {SEVERITY_ICON[issue.severity]}
                </div>
                <div className="sv-content">
                  <div className="sv-msg">{issue.message}</div>
                  {issue.original && issue.suggestion && (
                    <div className="sv-diff">
                      <span className="sv-original">{issue.original.slice(0, 50)}</span>
                      <span>→</span>
                      <span className="sv-suggestion">{issue.suggestion.slice(0, 50)}</span>
                    </div>
                  )}
                </div>
                {issue.suggestion && issue.original && (
                  <button className="sv-apply-btn" onClick={() => handleApplyOne(issue)}>
                    Apply
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
